import { type FeatureFlagKey, featureFlags } from './flags';

const TRUE_VALUES = new Set(['', '1', 'true', 'on', 'yes']);
const FALSE_VALUES = new Set(['0', 'false', 'off', 'no']);

type FlagQuery = {
  values: Partial<Record<FeatureFlagKey, string | boolean>>;
  clear: boolean;
  cleanSearch: URLSearchParams;
};

export function parseFlagQuery(search: URLSearchParams): FlagQuery | null {
  const controlKeys = [...search.keys()].filter((key) =>
    key === 'enableFeatureFlag' || key === 'disableFeatureFlag' || key === 'clearFeatureFlags' || key.startsWith('ff_'),
  );
  if (controlKeys.length === 0) return null;

  const cleanSearch = new URLSearchParams(search);
  for (const key of controlKeys) cleanSearch.delete(key);
  const values: FlagQuery['values'] = {};
  if (search.get('clearFeatureFlags') === 'true') return { values, clear: true, cleanSearch };

  for (const parameter of ['enableFeatureFlag', 'disableFeatureFlag']) {
    for (const key of search.getAll(parameter).flatMap((value) => value.split(',').map((name) => name.trim()))) {
      const flag = featureFlags.find((candidate) => candidate.key === key);
      if (flag) {
        values[flag.key] = parameter === 'enableFeatureFlag' && flag.type === 'boolean' ? true : flag.defaultValue;
      }
    }
  }

  for (const [key, value] of search) {
    if (!key.startsWith('ff_')) continue;
    const flag = featureFlags.find((candidate) => candidate.key === key.slice(3));
    if (!flag) continue;
    if (flag.type === 'boolean') {
      const normalized = value.trim().toLowerCase();
      if (TRUE_VALUES.has(normalized)) values[flag.key] = true;
      else if (FALSE_VALUES.has(normalized)) values[flag.key] = false;
    } else if (flag.variants.includes(value as never)) {
      values[flag.key] = value;
    }
  }
  return { values, clear: false, cleanSearch };
}
